<template>
  <v-dialog v-model="showDialog" max-width="620" scrollable>
    <v-card>
      <v-card-title class="d-flex">
        Manage Daily Checklist
        <v-spacer></v-spacer>
        <v-btn icon @click="showDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p class="body-2 grey--text text--darken-1">
          Items on this list appear as switches on the Daily Facility Checklist
          for every branch. Retiring an item hides it from new checks but keeps
          it in past history.
        </p>

        <!-- Add new item -->
        <v-row dense align="center">
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="newLabel"
              label="New checklist item"
              outlined
              dense
              hide-details
              @keyup.enter="addOption"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="newSection"
              :items="sectionOptions"
              label="Section"
              outlined
              dense
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-btn
              color="primary"
              block
              :loading="adding"
              :disabled="!newLabel.trim()"
              @click="addOption"
            >
              <v-icon left>mdi-plus</v-icon> Add
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
        ></v-progress-linear>

        <v-simple-table v-else dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th style="width: 38%">Label</th>
                <th style="width: 15%">Section</th>
                <th style="width: 12%">Order</th>
                <th style="width: 13%">Status</th>
                <th style="width: 22%" class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="opt in options" :key="opt.id">
                <td>
                  <v-text-field
                    outlined
                    v-model="opt.label"
                    dense
                    hide-details
                    :disabled="!opt.is_active"
                  ></v-text-field>
                </td>
                <td>
                  <v-chip
                    small
                    outlined
                    :color="opt.section === 'security' ? 'deep-purple' : 'blue'"
                  >
                    {{ opt.section === "security" ? "Security" : "Daily" }}
                  </v-chip>
                </td>
                <td>
                  <v-text-field
                    outlined
                    v-model.number="opt.sort_order"
                    type="number"
                    dense
                    hide-details
                    style="max-width: 70px"
                    :disabled="!opt.is_active"
                  ></v-text-field>
                </td>
                <td>
                  <v-chip small :color="opt.is_active ? 'green' : 'grey'" dark>
                    {{ opt.is_active ? "Active" : "Retired" }}
                  </v-chip>
                </td>
                <td class="text-right">
                  <v-btn
                    icon
                    small
                    color="primary"
                    :loading="savingId === opt.id"
                    :disabled="!opt.is_active"
                    @click="saveOption(opt)"
                  >
                    <v-icon small>mdi-content-save</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="red"
                    :disabled="!opt.is_active"
                    @click="retire(opt)"
                  >
                    <v-icon small>mdi-archive</v-icon>
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!options.length">
                <td colspan="5" class="text-center grey--text">
                  No checklist items yet.
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  props: ["show"],
  computed: {
    ...mapGetters({
      options: "facilities/daily_check_options",
      hasPermission: "permission/hasPermission",
    }),
    sectionOptions() {
      const opts = [{ text: "Daily", value: "daily" }];
      if (this.hasPermission("facilities.edit_security_systems")) {
        opts.push({ text: "Security & Systems", value: "security" });
      }
      return opts;
    },
    showDialog: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit("update:show", value);
      },
    },
  },
  data() {
    return {
      loading: false,
      adding: false,
      savingId: null,
      newLabel: "",
      newSection: "daily",
    };
  },
  watch: {
    show(value) {
      if (value) this.load();
    },
  },
  methods: {
    ...mapActions({
      get_daily_check_options: "facilities/get_daily_check_options",
      add_daily_check_option: "facilities/add_daily_check_option",
      edit_daily_check_option: "facilities/edit_daily_check_option",
      retire_daily_check_option: "facilities/retire_daily_check_option",
    }),
    async load() {
      this.loading = true;
      try {
        await this.get_daily_check_options({ include_inactive: 1 });
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    async addOption() {
      const label = this.newLabel.trim();
      if (!label) return;
      this.adding = true;
      try {
        const section = this.newSection || "daily";
        const nextOrder =
          this.options
            .filter((o) => o.section === section)
            .reduce((m, o) => Math.max(m, o.sort_order || 0), 0) + 1;
        await this.add_daily_check_option({
          label,
          section,
          sort_order: nextOrder,
        });
        this.newLabel = "";
        this.$emit("changed");
      } catch (e) {
        this.errorAlert(e, "Failed to add checklist item.");
      }
      this.adding = false;
    },
    async saveOption(opt) {
      if (!opt.label || !opt.label.trim()) {
        this.$alert({
          title: "Required",
          message: "Label cannot be empty.",
          color: "error",
        });
        return;
      }
      this.savingId = opt.id;
      try {
        await this.edit_daily_check_option({
          id: opt.id,
          label: opt.label.trim(),
          sort_order: opt.sort_order || 0,
          is_active: opt.is_active,
        });
        this.$emit("changed");
      } catch (e) {
        this.errorAlert(e, "Failed to update checklist item.");
      }
      this.savingId = null;
    },
    async retire(opt) {
      const ok = await this.$confirm(
        `Retire "${opt.label}"? It will no longer appear on new checklists.`
      );
      if (!ok) return;
      try {
        await this.retire_daily_check_option(opt.id);
        this.$emit("changed");
      } catch (e) {
        this.errorAlert(e, "Failed to retire checklist item.");
      }
    },
    errorAlert(e, fallback) {
      console.error(e);
      this.$alert({
        title: "Error",
        message:
          e.response && (e.response.status === 422 || e.response.status === 403)
            ? e.response.data.message
            : fallback,
        color: "error",
      });
    },
  },
};
</script>
